import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrandMark } from "../components/Brand";
import { Input } from "../components/ui/input";
import { api, formatCurrency, formatDate, formatApiError, openAuthedPdf } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import {
  ArrowLeft, Search, FileText, FolderOpen, Loader2, ExternalLink, Receipt, FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import ImpersonationBanner from "../components/ImpersonationBanner";
import { getAttorney } from "../lib/attorneyAuth";

export default function AttorneyDocumentVault() {
  useSEO("Documents — Instabridge firm portal", "All your firm's agreements, payoff letters, and reductions in one place.");
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/documents`);
      setDocs(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/attorney-login", { replace: true });
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => docs.filter((d) => {
    if (kind !== "all" && d.kind !== kind) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(d.title || "").toLowerCase().includes(s) && !(d.client_name || "").toLowerCase().includes(s)) return false;
    }
    return true;
  }), [docs, kind, search]);

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="attorney-vault">
      <ImpersonationBanner impersonation={(getAttorney() || {}).impersonation} firm={(getAttorney() || {}).firm} />
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <div className="mx-auto max-w-[1300px] px-5 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/attorney/portal"><BrandMark /></Link>
          <Link to="/attorney/portal" className="text-xs text-[#6B7280] hover:text-[#0A2540] inline-flex items-center gap-1" data-testid="vault-back"><ArrowLeft className="w-3.5 h-3.5" /> Portal</Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1300px] px-5 lg:px-8 py-7">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <FolderOpen className="w-3.5 h-3.5" /> Document vault
          </div>
          <h1 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold tracking-tight">All your firm's documents</h1>
          <p className="text-sm text-[#6B7280] mt-1.5">Agreements, payoff statements, and reductions — searchable, downloadable, always up-to-date.</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by client or document name…" className="pl-9 h-9 border-[#E5E7EB]" data-testid="vault-search" />
            </div>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-[#F3F4F6]">
              {[
                { v: "all", l: "All" },
                { v: "contract", l: "Agreements" },
                { v: "payoff", l: "Payoffs" },
              ].map((t) => (
                <button key={t.v} onClick={() => setKind(t.v)} data-testid={`vault-filter-${t.v}`}
                  className={`px-3 h-7 rounded-md text-xs font-medium transition-colors ${kind === t.v ? "bg-white text-[#0A2540] shadow-sm" : "text-[#6B7280] hover:text-[#0A2540]"}`}>
                  {t.l}
                </button>
              ))}
            </div>
            <div className="ml-auto text-xs text-[#6B7280] tabular-nums">{filtered.length} of {docs.length}</div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-[#6B7280] inline-flex items-center gap-2 w-full justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center" data-testid="vault-empty">
              <div className="inline-flex flex-col items-center text-[#6B7280]">
                <FolderOpen className="w-7 h-7 mb-2 text-[#9CA3AF]" />
                <div className="text-sm">Nothing here yet.</div>
                <div className="text-xs mt-1">Generated agreements and payoff letters will appear instantly.</div>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-[#F3F4F6]">
              {filtered.map((d) => (
                <li key={`${d.kind}-${d.id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-[#F9FAFB] group" data-testid={`vault-row-${d.kind}-${d.id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${d.kind === "contract" ? "bg-[#EFF6FF] text-[#1D4ED8]" : "bg-[#FEF3C7] text-[#D97706]"}`}>
                    {d.kind === "contract" ? <FileSignature className="w-4 h-4" /> : <Receipt className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#0A2540] truncate">{d.title}</div>
                    <div className="text-xs text-[#6B7280] truncate">{d.client_name || "—"} · {d.kind === "payoff" ? `${formatCurrency(d.amount)} · valid through ${formatDate(d.valid_through)}` : `${d.status || "Draft"}`}</div>
                  </div>
                  <div className="text-xs text-[#6B7280] tabular-nums hidden sm:block">{formatDate(d.created_at)}</div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const fname = `${d.title || "document"}.pdf`.replace(/[^A-Za-z0-9._-]+/g, "_");
                        await openAuthedPdf(d.pdf_url, fname);
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF] text-[#374151] text-xs font-medium transition-colors"
                    data-testid={`vault-open-${d.kind}-${d.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" /> Open
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
